import { useState, useMemo } from 'react';
import type { CurrentRead } from '@signalvote/data';
import type { CanvasserStatsItem, CampaignBenchmarks } from '@signalvote/shared';
import type { ContactAnalyticsResponse } from '@signalvote/shared';
import { TrendIcon } from './TrendIcon';
import { calculateShiftHours, formatShiftDate } from '@/lib/utils';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { CanvasserTypeBadge } from '@/components/admin/staff/shared';
import { ContactOutcomeChart } from './ContactOutcomeChart';
import { PerformanceTrendChart } from './PerformanceTrendChart';
import { getReliabilityColor, getSurveyColor } from './colorUtils';
import type { CanvasserPerformanceResponse, ShiftListResponse } from '@signalvote/shared';
import type { SortDirection } from './types';

// ============================================================================
// Types
// ============================================================================

interface CanvasserDetailProps {
  userId: string;
  canvasserStats: CanvasserStatsItem[];
  shifts: CurrentRead<ShiftListResponse>;
  contactAnalytics: ContactAnalyticsResponse | undefined;
  isLoadingStats: boolean;
  isLoadingContacts: boolean;
  onBack: () => void;
  benchmarks?: CampaignBenchmarks;
  performanceData?: CanvasserPerformanceResponse;
  isLoadingPerformance?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function CanvasserDetail({
  userId,
  canvasserStats,
  shifts,
  contactAnalytics,
  isLoadingStats,
  isLoadingContacts,
  onBack,
  benchmarks,
  performanceData,
  isLoadingPerformance,
}: CanvasserDetailProps) {
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const canvasser = canvasserStats.find((c) => c.userId === userId);

  const sortedShifts = useMemo(() => {
    if (shifts.status !== 'ready') return [];
    return [...shifts.data.items].sort((a, b) => {
      const cmp = a.date.localeCompare(b.date);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [shifts, sortDirection]);

  const handleSortToggle = () => {
    setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  // Loading state
  if (isLoadingStats) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  // Not found
  if (!canvasser) {
    return (
      <div className="space-y-4">
        <button
          onClick={onBack}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Analytics
        </button>
        <p className="py-8 text-center text-muted-foreground">Canvasser not found</p>
      </div>
    );
  }

  const fullName = `${canvasser.firstName} ${canvasser.lastName}`;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-muted-foreground">
        <button onClick={onBack} className="hover:text-foreground">
          Analytics
        </button>
        <span>/</span>
        <span className="text-foreground">{fullName}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xl font-semibold">{fullName}</h2>
        <CanvasserTypeBadge type={canvasser.canvasserType} />
        {canvasser.vendorName && (
          <span className="text-sm text-muted-foreground">{canvasser.vendorName}</span>
        )}
        {canvasser.teamNames.map((team) => (
          <span
            key={team}
            className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-xs"
          >
            {team}
          </span>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <Card data-testid="stat-card-shifts">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Shifts Completed</p>
            <p className="text-2xl font-bold tabular-nums">{canvasser.shiftsCompleted}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Hours Worked</p>
            <p className="text-2xl font-bold tabular-nums">{canvasser.hoursWorked.toFixed(1)}</p>
          </CardContent>
        </Card>
        <Card data-testid="stat-card-doors">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Doors Knocked</p>
            <p className="text-2xl font-bold tabular-nums">
              {canvasser.doorsKnocked}
              <TrendIcon className="ml-1 inline" direction={canvasser.trendDoorsKnocked} />
            </p>
            {canvasser.hoursWorked > 0 && benchmarks?.avgDoorsPerHour != null && (
              <p className="text-xs text-muted-foreground">
                {(canvasser.doorsKnocked / canvasser.hoursWorked).toFixed(1)}/hr vs {benchmarks.avgDoorsPerHour.toFixed(1)} avg
              </p>
            )}
          </CardContent>
        </Card>
        <Card data-testid="stat-card-contacts">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Contacts Made</p>
            <p className="text-2xl font-bold tabular-nums">
              {canvasser.contactsMade}
              <TrendIcon className="ml-1 inline" direction={canvasser.trendContactsMade} />
            </p>
            {canvasser.shiftsCompleted > 0 && benchmarks?.avgContactsPerShift != null && (
              <p className="text-xs text-muted-foreground">
                {(canvasser.contactsMade / canvasser.shiftsCompleted).toFixed(1)}/shift vs {benchmarks.avgContactsPerShift.toFixed(1)} avg
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Reliability</p>
            <p className={`text-2xl font-bold tabular-nums ${getReliabilityColor(canvasser.reliabilityRate)}`}>
              {canvasser.reliabilityRate !== null ? `${canvasser.reliabilityRate.toFixed(1)}%` : 'N/A'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Survey %</p>
            <p className={`text-2xl font-bold tabular-nums ${getSurveyColor(canvasser.surveyCompletionRate)}`}>
              {canvasser.surveyCompletionRate !== null ? `${canvasser.surveyCompletionRate.toFixed(1)}%` : 'N/A'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Shift History Table */}
      <Card className="p-6">
        <h3 className="mb-4 text-lg font-semibold">Shift History</h3>

        {shifts.status === 'failed' || shifts.status === 'offline' ? (
          <p role="alert" className="py-8 text-center text-sm text-destructive">
            {shifts.status === 'offline' ? "You're offline, so shifts can't load." : "Couldn't load shifts. Try again shortly."}
          </p>
        ) : shifts.status !== 'ready' ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : sortedShifts.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">No shift data</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th
                    className="cursor-pointer pb-2 pr-4 hover:text-foreground"
                    onClick={handleSortToggle}
                  >
                    Date {sortDirection === 'desc' ? '↓' : '↑'}
                  </th>
                  <th className="pb-2 pr-4">Walkbook</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4 text-right">Hours</th>
                  <th className="pb-2 pr-4 text-right">Doors</th>
                  <th className="pb-2 text-right">Contacts</th>
                </tr>
              </thead>
              <tbody>
                {sortedShifts.map((shift) => {
                  const hours = calculateShiftHours(shift.startedAt, shift.completedAt);
                  return (
                    <tr key={shift.id} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 tabular-nums">{formatShiftDate(shift.date)}</td>
                      <td className="py-2 pr-4">{shift.walkbookName ?? '—'}</td>
                      <td className="py-2 pr-4">
                        {shift.effectiveStatus === 'NoShow' ? (
                          <Badge variant="destructive">No-Show</Badge>
                        ) : (
                          shift.effectiveStatus
                        )}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {hours !== null ? hours.toFixed(1) : '—'}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {shift.doorsKnocked ?? 0}
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {shift.contactsMade ?? 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Performance Trend Chart */}
      <PerformanceTrendChart
        data={performanceData}
        isLoading={isLoadingPerformance ?? false}
      />

      {/* Contact Outcome Chart */}
      <ContactOutcomeChart
        contactsByStatus={contactAnalytics?.contactsByStatus ?? []}
        isLoading={isLoadingContacts}
      />
    </div>
  );
}
